import {isDevMode} from '@angular/core';
import {TranslateLoader} from '@ngx-translate/core';
import {Observable, of, combineLatest} from 'rxjs';
import {HttpClient, HttpParams} from '@angular/common/http';
import {catchError, map, tap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';

export class TranslateProvider implements TranslateLoader {

  constructor(
    private http: HttpClient
  ) {
  }

  static getCommonTranslation(http: HttpClient, lang: string): Observable<any> {
    return http.get(`assets/i18n/${lang}.json`).pipe(
      map(translations => TranslateProvider.replacePlaceholders(translations)), // Use the private replacePlaceholders method
      catchError(() => of({})),
    );
  }

  /**
   * Replaces placeholders like {{variable}} in the translation strings
   * with their corresponding runtime values from the `environment` file.
   */
  private static replacePlaceholders(translations: Record<string, any>): Record<string, any> {
    const environmentVariables = {
      // Add other dynamic environment variables as needed
      projectFullName: environment.projectFullName,
      projectName: environment.projectName,
      contactEmail: environment.contactEmail,
      termsOfUse: environment.termsOfUse,
      privacyPolicy: environment.privacyPolicy,
      termsOfUse_cat: environment.termsOfUse_cat || environment.termsOfUse,
      privacyPolicy_cat: environment.privacyPolicy_cat || environment.privacyPolicy,
      termsOfUse_esp: environment.termsOfUse_esp || environment.termsOfUse,
      privacyPolicy_esp: environment.privacyPolicy_esp || environment.privacyPolicy,
      termsOfUse_eng: environment.termsOfUse_eng || environment.termsOfUse,
      privacyPolicy_eng: environment.privacyPolicy_eng || environment.privacyPolicy,
      termsOfUse_nld: environment.termsOfUse_nld || environment.termsOfUse,
      privacyPolicy_nld: environment.privacyPolicy_nld || environment.privacyPolicy,
    };

    // Recursively replace placeholders in the translations object
    const replace = (value: any): any => {
      if (typeof value === 'string') {
        // Replace {{variable}} with the corresponding environment variable value, if it exists
        return value.replace(/{{(\w+)}}/g, (_, key) =>
          environmentVariables.hasOwnProperty(key) ? environmentVariables[key] : `{{${key}}}`
        );
      } else if (typeof value === 'object' && value !== null) {
        // If the value is an object (nested), process each key
        Object.keys(value).forEach(innerKey => {
          value[innerKey] = replace(value[innerKey]);
        });
      }
      return value;
    };

    return replace(translations);
  }

  getTranslation(lang: string): Observable<any> {
    const project = JSON.parse(localStorage.getItem('project_id'));
    if (project && project.projectID) {
      return combineLatest([
        TranslateProvider.getCommonTranslation(this.http, lang), // Already applies replacePlaceholders
        this.getProjectDictionary(project.projectID.id, lang),
      ]).pipe(
        map(result => {
          // Merge all translations into one object
          return Object.assign({}, result[0], result[1]);
          // No need to call replacePlaceholders here for result[0] as it's already applied in getCommonTranslation
        }),
      );
    } else {
      // No need to process translations again, as getCommonTranslation now handles replacePlaceholders
      return TranslateProvider.getCommonTranslation(this.http, lang);
    }
  }

  getProjectDictionary(project_id, language) {
    const params = new HttpParams().append('project_id', project_id).append('language', language);
    return this.http.get(`/api2/member/project_dictionary`, {params}).pipe(
      catchError(() => [{'map': {}}]),
      map(res => res['map']),
    );
  }

}
